import { Alert, Box, Card, CardHeader, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { ReactElement, useEffect, useMemo, useState } from 'react';

import {
  computeNumLicensesUsed,
  formatIntervalMonths,
  formatMonetaryAmount,
  getEffectiveDiscount,
  getPlanEstimations,
  getTaxIdType,
  roundMonthsToWholeIntervals,
  useSnackbar,
} from '@localstack/services';

import {
  AcquisitionChannel,
  CreditCard,
  Discount,
  LicenseAssignment,
  OrderContext,
  GetOrganizationResponse,
  UserMembershipView,
  OrganizationType,
  Plan,
  Product,
  Subscription,
  TaxDetails,
  PatchOrganizationRequest,
} from '@localstack/types';

import { FieldErrors, FormProvider, useForm } from 'react-hook-form';

import { LoadingFragment, ProgressButton } from '../../../feedback';

import { getRenewalDate, pickCustomForever } from '../../../utils';

import { CheckoutLegalFineprintText } from '../../../display';

import { DowngradeLicensesList } from '../DowngradeLicensesList';

import { AdminCustomization } from './components/AdminCustomization';
import { CheckoutWarnings } from './components/CheckoutWarnings';
import { PlanSelector } from './components/PlanSelector';
import { PurchaseSummary } from './components/PurchaseSummary';
import { CheckoutForm } from './components/CheckoutForm';
import { getFirstValidationError, getSortedPlans } from './utils';

export interface CheckoutFormProps {
  loading: boolean;
  isSubMutating: boolean;
  taxLoading: boolean;
  tax: Optional<TaxDetails>;
  defaultPlan?: Optional<Plan>;
  plans: Plan[];
  cards: CreditCard[];
  subscription: Optional<Subscription>;
  subscriptionLicenseAssignments?: Optional<LicenseAssignment[]>;
  organization: Optional<GetOrganizationResponse>;
  members?: UserMembershipView[];
  promotion: Optional<Discount>;
  promotionLoading: boolean;
  adminMode?: boolean;
  /* all available discounts, use it with `adminMode=true` */
  adminDiscounts?: Discount[];
  /* all available products, use it with `adminMode=true` */
  adminProducts?: Product[];
  showCardForm: boolean;
  /**
   * When true, the Freemium plan family is hidden from the picker. Set by the caller
   * from org restrictions (OrgRestrictionReason.FREEMIUM_HOBBY_COMMERCIAL_MISUSE).
   */
  isFreemiumHobbyCommercialMisuse?: boolean;
  /* a bit ugly way to keep stripe libs out of ui package */
  renderCardForm?: (currency: string) => ReactElement;
  onChangeSeats: (seats: number) => unknown;
  onChangePlan: (plan: Plan) => unknown;
  onToggleCardForm: () => unknown;
  onApplyPromoCode: (planId: string, code: Optional<string>) => unknown;
  onUpdateOrganization: (data: PatchOrganizationRequest) => unknown;
  onDeleteCard: (card: CreditCard) => unknown;
  onDeleteLicense?: (id: string) => unknown;
  onSubscribe: (
    /** The plan ID of the selected plan */
    planId: string,
    /** Product ids that the subscription should have */
    productIds: string[],
    /** The order context containing information the subscription will be created/updated with */
    context: OrderContext,
    /** The ID of the current subscription to be updated */
    subscriptionId?: string,
    /** The discount ID to be applied to the subscription */
    discountId?: string,
  ) => Promise<void>;
}

type CheckoutFormData = {
  address?: string;
  email?: string;
  country?: string;
  phone?: string;
  zip?: string;
  city?: string;
  state?: string;
  name?: string;
  tax_id?: string;
  orgType?: OrganizationType;
  seats: number;
};

export const CheckoutFormSkeleton = (): ReactElement => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={8}>
      <LoadingFragment variant="card" height={650} loading />
    </Grid>
    <Grid item xs={12} md={4}>
      <LoadingFragment variant="card" height={300} loading />
      <Box mt={2} />
      <LoadingFragment variant="list" size={4} height={40} loading />
      <Box mt={2} />
      <LoadingFragment variant="list" size={4} height={16} loading />
    </Grid>
  </Grid>
);

export const CheckoutComponent = ({
  loading,
  isSubMutating,
  taxLoading,
  tax,
  defaultPlan,
  plans,
  cards,
  subscription,
  subscriptionLicenseAssignments,
  organization,
  members,
  promotion,
  promotionLoading,
  adminMode,
  adminDiscounts,
  adminProducts,
  showCardForm,
  isFreemiumHobbyCommercialMisuse,
  renderCardForm,
  onChangeSeats,
  onChangePlan,
  onToggleCardForm,
  onApplyPromoCode,
  onUpdateOrganization,
  onDeleteCard,
  onDeleteLicense,
  onSubscribe,
}: CheckoutFormProps): ReactElement => {
  const [seats, setSeats] = useState(subscription?.seats ?? 1);

  const [discount, setDiscount] = useState<Optional<Discount>>(null);
  const [promoCode, setPromoCode] = useState<Optional<string>>(null);
  const [overrides, setOverrides] = useState<Partial<OrderContext>>({});
  const [card, setCard] = useState<Optional<CreditCard>>(cards?.[0]);
  const [validationError, setValidationError] = useState<Optional<string>>(null);

  const sortedPlans = useMemo(
    () => getSortedPlans(plans, subscription, adminMode, defaultPlan, isFreemiumHobbyCommercialMisuse),
    [plans, isFreemiumHobbyCommercialMisuse],
  );

  const [plan, setPlan] = useState<Plan>(defaultPlan || (sortedPlans[0] as Plan));
  const [products, setProducts] = useState<Product[]>(plan?.products);

  const filteredCards = useMemo(() => cards.filter((c) => c.currency === plan?.currency), [cards, plan?.currency]);

  const effective = getEffectiveDiscount({ promotion, plan, subscription });
  const resolvedDiscount = adminMode ? discount : effective.discount;
  const renewalDiscount = adminMode ? discount : effective.renewalDiscount;

  const computedCustomRenewalDate = getRenewalDate(overrides, plan.interval_months, subscription, adminMode);

  // final context with overrides
  const context: OrderContext = {
    seats,

    org_id: '', // assigned by the backend
    customer_id: '', // assigned by the backend
    payment_id: card?.id,
    promo_code: resolvedDiscount && promoCode ? promoCode : undefined,

    interval: subscription ? subscription.interval_months : plan.interval_months,
    name: subscription?.name,
    acquisition_channel: subscription?.acquisition_channel,
    billing_channel: subscription?.billing_channel,
    ...(adminMode && {
      custom_renewal_date: computedCustomRenewalDate,
      mrr: subscription?.estimated_mrr ?? undefined,
      mrr_private: subscription?.estimated_private_mrr ?? undefined,
    }),
    ...overrides,
  };

  const customMrr = context.mrr ? context.mrr * (context.interval ?? plan.interval_months) : null;

  const planFullAmount = customMrr ?? getPlanEstimations(products, context, resolvedDiscount, false);
  const planFormattedFullAmount = formatMonetaryAmount(planFullAmount, plan.currency);

  const isUpgrading = subscription && subscription.seats < context.seats;

  const licensesUsed =
    (subscription &&
      subscriptionLicenseAssignments &&
      organization &&
      computeNumLicensesUsed(subscription, subscriptionLicenseAssignments)) ||
    0;

  const hasOutstandingKeys = Boolean(subscription && licensesUsed - context.seats > 0);
  const isDowngrading = subscription && subscription.seats > context.seats;
  const requiresPayment = getPlanEstimations(products, context, resolvedDiscount, true) > 0;

  const isAdminCreate = adminMode && !subscription;
  const isAdminUpdate = adminMode && subscription;
  const isAdminCreateOrUpdate = isAdminCreate || isAdminUpdate;

  useEffect(() => {
    onChangeSeats?.(seats);
  }, [seats]);

  // Non-admin flow: sync discount by priority → promotion → selected plan.
  // Why: promos should override; subscription discount only applies on same plan,
  // or when upgrading from a non‑paying plan (trial/freemium/hobby/community).
  // Avoid redundant updates by comparing IDs.
  useEffect(() => {
    if (isAdminCreateOrUpdate) return;
    setDiscount((prev) => (prev?.id === effective.discount?.id ? prev : effective.discount));
  }, [isAdminCreateOrUpdate, effective.discount?.id]);

  // Admin flow when NOT self-serve i.e. sales-led sub:
  // for sub create flow: when NOT self-serve, force the forever-100% (“Custom Payment”) discount.
  // for sub update flow: use the subs own discount i.e. which will be 100% as it'll be a sales-led sub.
  useEffect(() => {
    if (!isAdminCreateOrUpdate) return;

    const acq = isAdminCreate ? overrides.acquisition_channel : subscription?.acquisition_channel;
    if (acq === AcquisitionChannel.SELF_SERVE) return;

    const forced = pickCustomForever(adminDiscounts);
    const newDiscount = isAdminCreate ? forced : subscription?.discount;
    setDiscount((prev) => (prev?.id === newDiscount?.id ? prev : newDiscount));
  }, [isAdminCreateOrUpdate, isAdminCreate, overrides, adminDiscounts, discount?.id, subscription?.id]);

  // Admin + Self-serve: mirror discount from promotion → plan, without forcing Custom Payment.
  // Only update when the target discount ID actually changes (avoids redundant state).
  useEffect(() => {
    if (!isAdminCreate) return;

    const acq = overrides.acquisition_channel;
    if (acq !== AcquisitionChannel.SELF_SERVE) return;

    const next = promotion || plan.discount || null;
    setDiscount((prev) => (prev?.id === next?.id ? prev : next));
  }, [isAdminCreate, overrides.acquisition_channel, promotion, plan.id]);

  const handleChangePlan = (newPlan: Plan) => {
    // if subscription is not set we default products and
    // discount selection based on selected plan
    if (!subscription && !adminMode) {
      setDiscount(newPlan.discount);
    }

    // set the plan itself
    setPlan(newPlan);
    // propagate plan change for tax calculations
    onChangePlan?.(newPlan);

    // show products for selected plan
    setProducts(newPlan.products);
    // reset card on plan change (in case there's a change in currency)
    setCard(filteredCards?.[0]);

    if (promotion && promoCode) onApplyPromoCode?.(newPlan.id, promoCode);

    const maxNumberOfSeats = newPlan.plan_seat_limit ?? Infinity;
    if (!adminMode && seats > maxNumberOfSeats) {
      setSeats(maxNumberOfSeats);
    }
  };

  const onUpdateOverrides = (updates: Partial<OrderContext>) => setOverrides((prev) => ({ ...prev, ...updates }));

  const methods = useForm<CheckoutFormData>({
    mode: 'onBlur',
    defaultValues: { orgType: OrganizationType.INDIVIDUAL, seats: 1 },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = methods;

  const { showSnackbar } = useSnackbar();

  // todo: properly implement the form
  useEffect(() => {
    if (!organization) {
      return;
    }

    Object.entries(organization.basic_details ?? {}).forEach(([k, v]) => setValue(k as keyof CheckoutFormData, v));
    Object.entries(organization.billing_details ?? {}).forEach(([k, v]) => setValue(k as keyof CheckoutFormData, v));
    setValue('orgType', organization.basic_details.org_type);
    setValue('tax_id', organization.tax_details.tax_id);
  }, [organization?.id]);

  const onSubmit = async (data: CheckoutFormData) => {
    try {
      const firstValidationError = getFirstValidationError({
        plan,
        subscription,
        hasOutstandingKeys,
        adminMode,
        isCardRequired: requiresPayment,
        card,
      });

      setValidationError(firstValidationError);

      if (firstValidationError) {
        showSnackbar({
          message: 'Failed to complete purchase',
          severity: 'error',
        });
        return;
      }

      if (organization) {
        const organizationData: PatchOrganizationRequest = {
          basic_details: {
            name: data?.name ?? organization?.basic_details.name,
            org_type: data.orgType,
          },
          billing_details: {
            address: data.address,
            city: data.city,
            country: data.country,
            email: data.email,
            phone: data.phone,
            state: data.state,
            zip: data.zip,
          },
          ...(data.orgType === OrganizationType.COMPANY && {
            tax_details: {
              tax_id: data.tax_id,
              tax_id_type: getTaxIdType(data.country, data.tax_id) ?? undefined,
            },
          }),
        };

        await onUpdateOrganization(organizationData);
      }

      const isAdminCreatingSalesLed = isAdminCreate && context.acquisition_channel !== AcquisitionChannel.SELF_SERVE;
      const appliedDiscount = isAdminCreatingSalesLed
        ? (pickCustomForever(adminDiscounts) ?? resolvedDiscount)
        : resolvedDiscount;

      onSubscribe?.(
        plan.id,
        products?.map((p) => p.id),
        context,
        subscription?.id,
        appliedDiscount?.id,
      );
    } catch (error) {
      showSnackbar({
        message: 'Something went wrong, please try again later or contact support',
        severity: 'error',
      });
      console.error('Checkout failed:', error);
    }
  };

  const onError = (errors: FieldErrors) => {
    const firstError = Object.values(errors)[0];
    const errorMessage = firstError?.message || 'Please fill in all required fields.';

    setValidationError(errorMessage);

    showSnackbar({
      message: 'Failed to complete purchase',
      severity: 'error',
    });
  };

  if (isSubMutating) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 2.25,
          flexDirection: 'column',
        }}
      >
        <CircularProgress color="primary" />
        <Typography>Processing payment please wait...</Typography>
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <Stack spacing={3}>
        <CheckoutWarnings subscription={subscription} plan={plan} validationError={validationError} />
        {adminMode && (
          <AdminCustomization
            edit={!!subscription}
            plan={plan}
            context={context}
            products={adminProducts ?? []}
            selectedProducts={products}
            discounts={adminDiscounts ?? []}
            selectedDiscount={discount}
            onUpdateContext={onUpdateOverrides}
            onUpdateDiscount={setDiscount}
            onUpdateProducts={setProducts}
          />
        )}
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <PlanSelector
                adminMode={!!adminMode}
                tax={tax}
                sortedPlans={sortedPlans}
                plan={plan}
                handleChangePlan={handleChangePlan}
                context={context}
                seats={seats}
                setSeats={setSeats}
                subscription={subscription}
              />
              <CheckoutForm
                requiresPayment={requiresPayment}
                showCardForm={showCardForm}
                plan={plan}
                card={card}
                setCard={setCard}
                filteredCards={filteredCards}
                onDeleteCard={onDeleteCard}
                renderCardForm={renderCardForm}
                onToggleCardForm={onToggleCardForm}
              />
              {subscription && isDowngrading && hasOutstandingKeys && (
                <Card>
                  <CardHeader title="Reduce Number of Licenses" />
                  <Alert severity="warning">
                    Remove <b>{licensesUsed - context.seats}</b> assigned license(s) or API key(s) in order to reduce
                    the number of seats from {subscription.seats} to {seats}.
                  </Alert>
                  <DowngradeLicensesList
                    members={members ?? []}
                    licenseAssignments={subscriptionLicenseAssignments ?? []}
                    onDeleteLicense={(id) => onDeleteLicense?.(id)}
                  />
                </Card>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <PurchaseSummary
                plan={plan}
                products={products}
                context={context}
                discount={renewalDiscount}
                promotionLoading={promotionLoading}
                onApplyPromoCode={onApplyPromoCode}
                seats={seats}
                taxLoading={taxLoading}
                tax={tax}
                promoCode={promoCode}
                setPromoCode={setPromoCode}
                isAdminMode={!!adminMode}
                hasAppliedPromoCode={!!promotion && !!promoCode}
              />
              {/* Terms and Conditions */}
              <ProgressButton
                color="primary"
                variant="contained"
                size="large"
                type="submit"
                fullWidth
                disabled={isSubMutating || taxLoading || loading || promotionLoading}
                loading={isSubmitting}
                sx={{ marginBottom: 2 }}
              >
                Check Out
              </ProgressButton>
              <Box mt={2}>
                {((subscription && subscription.plan.id !== plan.id) || isUpgrading || isDowngrading) && (
                  <Typography variant="caption" align="justify" color="textSecondary" component="div">
                    Please note that in case of subscription upgrade or downgrade you will be charged a prorated amount
                    in the next billing cycle.
                  </Typography>
                )}
                {renewalDiscount && renewalDiscount?.duration_months && renewalDiscount?.duration_months !== -1 && (
                  <Typography variant="caption" align="justify" color="textSecondary" component="div">
                    Please note the total displayed is subject to a discount. We will start charging the full amount of{' '}
                    {planFormattedFullAmount} after the{' '}
                    {formatIntervalMonths(
                      roundMonthsToWholeIntervals(renewalDiscount.duration_months, plan.interval_months),
                    )}{' '}
                    of your subscription
                  </Typography>
                )}
              </Box>

              <Box mt={2}>
                <Typography variant="caption" align="justify" color="textSecondary" component="div">
                  <CheckoutLegalFineprintText />
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Stack>
    </FormProvider>
  );
};
